import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Download, X, Share, Plus, Smartphone, Monitor } from 'lucide-react';

/**
 * PWAInstallPrompt — proactively invites the user to install the app on their
 * home screen at first launch on any device (desktop, mobile, tablet).
 *
 * Behaviour:
 *  - On Chrome/Edge/Android: captures `beforeinstallprompt` and shows a
 *    native "Install" button. Fires the prompt immediately when clicked.
 *  - On iOS Safari (which does NOT fire beforeinstallprompt): shows a step
 *    by step guide to add to home screen via the Share menu.
 *  - On desktop Safari / other browsers without native prompt: shows a
 *    generic guide.
 *  - Auto-hides when the app is already running in standalone mode.
 *  - Remembers dismissal for 7 days (localStorage) — reshow later.
 *  - First-launch behavior: prompt is shown ~2s after page load if the
 *    browser hasn't already fired beforeinstallprompt yet.
 */

const DISMISS_KEY = 'pwa-install-dismissed-until';
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true // iOS
  );
}

function detectPlatform() {
  const ua = (navigator.userAgent || '').toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || (/mac/.test(ua) && 'ontouchend' in document);
  const isAndroid = /android/.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
  return { isIOS, isAndroid, isSafari };
}

function shouldShow() {
  if (isStandalone()) return false;
  const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
  if (until && Date.now() < until) return false;
  return true;
}

export const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    if (!shouldShow()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // First-launch proactive fallback: if the browser doesn't fire
    // beforeinstallprompt (iOS Safari, or already-installable but not fired),
    // still show our prompt so the user knows they can install.
    const timer = setTimeout(() => {
      if (shouldShow()) setVisible(true);
    }, 2000);

    // Hide when the user installs
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 365 * 24 * 3600 * 1000));
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(timer);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice.catch(() => ({ outcome: 'dismissed' }));
      setDeferred(null);
      if (outcome === 'accepted') {
        setVisible(false);
        return;
      }
      // If dismissed, snooze
      snooze();
      return;
    }
    // Fallback: show iOS/manual guide
    setIosGuideOpen(true);
  };

  const snooze = () => {
    const until = Date.now() + DISMISS_DAYS * 24 * 3600 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setVisible(false);
  };

  if (!visible) return null;

  const DeviceIcon = platform.isIOS || platform.isAndroid ? Smartphone : Monitor;

  return (
    <>
      <div
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[60] bg-white border border-purple-200 shadow-2xl rounded-xl p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 fade-in duration-500"
        data-testid="pwa-install-prompt"
        role="dialog"
        aria-live="polite"
      >
        <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white shrink-0 shadow-md">
          <DeviceIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            Ajouter ECOM Guinée à l&apos;écran d&apos;accueil
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            Accès en 1 clic depuis votre {platform.isIOS || platform.isAndroid ? 'téléphone' : 'ordinateur'}, chargement plus rapide, mode hors-ligne.
          </div>
          <div className="mt-2.5 flex gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={install}
              className="bg-purple-600 hover:bg-purple-700 h-8 text-xs"
              data-testid="pwa-install-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {deferred ? 'Installer' : 'Comment installer ?'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={snooze}
              className="h-8 text-xs text-slate-500"
              data-testid="pwa-install-dismiss"
            >
              Plus tard
            </Button>
          </div>
        </div>
        <button
          onClick={snooze}
          className="text-slate-400 hover:text-slate-600 shrink-0"
          aria-label="Fermer"
          data-testid="pwa-install-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Manual install guide (iOS Safari, desktop Safari, etc.) */}
      {iosGuideOpen && (
        <div
          className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setIosGuideOpen(false)}
          data-testid="pwa-guide-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 animate-in slide-in-from-bottom-6 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-lg font-semibold text-slate-900">Installer sur l&apos;écran d&apos;accueil</div>
                <div className="text-xs text-slate-500">Suivez ces étapes rapides</div>
              </div>
              <button
                onClick={() => setIosGuideOpen(false)}
                className="text-slate-400 hover:text-slate-600"
                data-testid="pwa-guide-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {platform.isIOS ? (
              <ol className="space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-purple-100 text-purple-700 font-semibold text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                  <div>
                    Appuyez sur l&apos;icône <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 bg-slate-100 rounded"><Share className="h-3.5 w-3.5" /> Partager</span>
                    en bas (ou en haut sur iPad) de Safari.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-purple-100 text-purple-700 font-semibold text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                  <div>
                    Faites défiler et sélectionnez
                    <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 bg-slate-100 rounded"><Plus className="h-3.5 w-3.5" /> Sur l&apos;écran d&apos;accueil</span>.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-purple-100 text-purple-700 font-semibold text-xs flex items-center justify-center shrink-0 mt-0.5">3</span>
                  <div>
                    Confirmez avec <b>Ajouter</b>. L&apos;icône ECOM Guinée apparaîtra sur votre écran d&apos;accueil.
                  </div>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-purple-100 text-purple-700 font-semibold text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                  <div>
                    Ouvrez le menu du navigateur (les <b>trois points ⋮</b> en haut à droite).
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-purple-100 text-purple-700 font-semibold text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                  <div>
                    Sélectionnez <b>Installer ECOM Guinée</b> ou <b>Ajouter à l&apos;écran d&apos;accueil</b>.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-purple-100 text-purple-700 font-semibold text-xs flex items-center justify-center shrink-0 mt-0.5">3</span>
                  <div>Confirmez pour ajouter le raccourci.</div>
                </li>
              </ol>
            )}

            <Button
              onClick={() => setIosGuideOpen(false)}
              className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
              data-testid="pwa-guide-ok"
            >
              J&apos;ai compris
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
